import _classCallCheck from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/createClass";
import _assertThisInitialized from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/assertThisInitialized";
import _get from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/get";
import _inherits from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/inherits";
import _possibleConstructorReturn from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/getPrototypeOf";
import _regeneratorRuntime from "/opt/build/repo/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/regenerator";

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

import { __awaiter } from "tslib";
import { Gain } from "../../core/context/Gain";
import { optionsFromArguments } from "../../core/util/Defaults";
import { readOnly } from "../../core/util/Interface";
import { Signal } from "../../signal/Signal";
import { WaveShaper } from "../../signal/WaveShaper";
import { Source } from "../Source";
import { Oscillator } from "./Oscillator";
import { generateWaveform } from "./OscillatorInterface";
/**
 * PulseOscillator is an oscillator with control over pulse width,
 * also known as the duty cycle. At 50% duty cycle (width = 0) the wave is
 * a square wave.
 * [Read more](https://wigglewave.wordpress.com/2014/08/16/pulse-waveforms-and-harmonics/).
 * ```
 *    width = -0.25        width = 0.0          width = 0.25
 *
 *   +-----+            +-------+       +    +-------+     +-+
 *   |     |            |       |       |            |     |
 *   |     |            |       |       |            |     |
 * +-+     +-------+    +       +-------+            +-----+
 *
 *
 *    width = -0.5                              width = 0.5
 *
 *     +---+                                 +-------+   +---+
 *     |   |                                         |   |
 *     |   |                                         |   |
 * +---+   +-------+                                 +---+
 *
 *
 *    width = -0.75                             width = 0.75
 *
 *       +-+                                 +-------+ +-----+
 *       | |                                         | |
 *       | |                                         | |
 * +-----+ +-------+                                 +-+
 * ```
 * @example
 * return Tone.Offline(() => {
 * 	const pulse = new Tone.PulseOscillator(50, 0.4).toDestination().start();
 * }, 0.1, 1);
 * @category Source
 */

export var PulseOscillator = /*#__PURE__*/function (_Source) {
  _inherits(PulseOscillator, _Source);

  var _super = _createSuper(PulseOscillator);

  function PulseOscillator() {
    var _this;

    _classCallCheck(this, PulseOscillator);

    _this = _super.call(this, optionsFromArguments(PulseOscillator.getDefaults(), arguments, ["frequency", "width"]));
    _this.name = "PulseOscillator";
    /**
     * gate the width amount
     */

    _this._widthGate = new Gain({
      context: _this.context,
      gain: 0
    });
    /**
     * Threshold the signal to turn it into a square
     */

    _this._thresh = new WaveShaper({
      context: _this.context,
      mapping: function mapping(val) {
        return val <= 0 ? -1 : 1;
      }
    });
    var options = optionsFromArguments(PulseOscillator.getDefaults(), arguments, ["frequency", "width"]);
    _this.width = new Signal({
      context: _this.context,
      units: "audioRange",
      value: options.width
    });
    _this._triangle = new Oscillator({
      context: _this.context,
      detune: options.detune,
      frequency: options.frequency,
      onstop: function onstop() {
        return _this.onstop(_assertThisInitialized(_this));
      },
      phase: options.phase,
      type: "triangle"
    });
    _this.frequency = _this._triangle.frequency;
    _this.detune = _this._triangle.detune; // connections

    _this._triangle.chain(_this._thresh, _this.output);

    _this.width.chain(_this._widthGate, _this._thresh);

    readOnly(_assertThisInitialized(_this), ["width", "frequency", "detune"]);
    return _this;
  }

  _createClass(PulseOscillator, [{
    key: "_start",
    value:
    /**
     * start the oscillator
     */
    function _start(time) {
      time = this.toSeconds(time);

      this._triangle.start(time);

      this._widthGate.gain.setValueAtTime(1, time);
    }
    /**
     * stop the oscillator
     */

  }, {
    key: "_stop",
    value: function _stop(time) {
      time = this.toSeconds(time);

      this._triangle.stop(time); // the width is still connected to the output.
      // that needs to be stopped also


      this._widthGate.gain.cancelScheduledValues(time);

      this._widthGate.gain.setValueAtTime(0, time);
    }
  }, {
    key: "_restart",
    value: function _restart(time) {
      this._triangle.restart(time);

      this._widthGate.gain.cancelScheduledValues(time);

      this._widthGate.gain.setValueAtTime(1, time);
    }
    /**
     * The phase of the oscillator in degrees.
     */

  }, {
    key: "phase",
    get: function get() {
      return this._triangle.phase;
    },
    set: function set(phase) {
      this._triangle.phase = phase;
    }
    /**
     * The type of the oscillator. Always returns "pulse".
     */

  }, {
    key: "type",
    get: function get() {
      return "pulse";
    }
    /**
     * The baseType of the oscillator. Always returns "pulse".
     */

  }, {
    key: "baseType",
    get: function get() {
      return "pulse";
    }
    /**
     * The partials of the waveform. Cannot set partials for this waveform type
     */

  }, {
    key: "partials",
    get: function get() {
      return [];
    }
    /**
     * No partials for this waveform type.
     */

  }, {
    key: "partialCount",
    get: function get() {
      return 0;
    }
    /**
     * *Internal use* The carrier oscillator type is fed through the
     * waveshaper node to create the pulse. Using different carrier oscillators
     * changes oscillator's behavior.
     */

  }, {
    key: "carrierType",
    set: function set(type) {
      this._triangle.type = type;
    }
  }, {
    key: "asArray",
    value: function asArray() {
      var length = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 1024;
      return __awaiter(this, void 0, void 0, /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                return _context.abrupt("return", generateWaveform(this, length));

              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, this);
      }));
    }
    /**
     * Clean up method.
     */

  }, {
    key: "dispose",
    value: function dispose() {
      _get(_getPrototypeOf(PulseOscillator.prototype), "dispose", this).call(this);

      this._triangle.dispose();

      this.width.dispose();

      this._widthGate.dispose();

      this._thresh.dispose();

      return this;
    }
  }], [{
    key: "getDefaults",
    value: function getDefaults() {
      return Object.assign(Source.getDefaults(), {
        detune: 0,
        frequency: 440,
        phase: 0,
        type: "pulse",
        width: 0.2
      });
    }
  }]);

  return PulseOscillator;
}(Source);